/**
 * Should opening this mail's attachment start an extraction in the background?
 *
 * A predicate in its own module, not a line inside the preview component, so it can be tested
 * without any component-test infra (this repo's vitest runs in a `node` environment with no DOM).
 * This function is THE decision, not a piece of it — every condition the spec states lives here,
 * so a call site cannot accidentally satisfy the rule by construction instead of by checking it.
 *
 * The confidence bar is `RFQ_THRESHOLD` — the SAME constant behind the `RFQ 87%` badge in the
 * message list. Reusing it rather than picking a second number means the badge the user sees and
 * the decision to spend an LLM call can never drift apart.
 *
 * A mail with no attachment is never pre-warmed (spec §3) — there's nothing yet for the local
 * fallback to read, and the only real call site today (`AttachmentPreview.tsx`'s preview modal)
 * happens to be unreachable without one anyway, but that must not be the only thing enforcing it:
 * a future call site with no such implicit guard would silently start firing LLM calls for
 * body-only mail otherwise.
 */

import { classifyRfqMail, isSupplierReply, RFQ_THRESHOLD, isOwnOutgoingRfq } from './mailClassify'
import { isInboundFolder } from './mail'
import type { MailMessage } from './types'

export function shouldPrewarm(mail: MailMessage): boolean {
  if (mail.attachments.length === 0) return false
  return classifyRfqMail(mail).confidence >= RFQ_THRESHOLD
}

/**
 * Should SELECTING this mail start an extraction in the background?
 *
 * Separate from `shouldPrewarm` above, which answers the narrower question for the attachment
 * preview modal. Two deliberate differences:
 *
 * 1. **No attachment is required.** The preview rule exists because that modal has nothing to
 *    show without a file. Selecting a mail is different: the local reader takes `body_text`, and
 *    plenty of real enquiries carry their whole item table in the body — verified on the live
 *    bench, where a body-only mail extracted four correctly-priced lines. Requiring an attachment
 *    here would skip exactly those.
 * 2. **A supplier's reply is never pre-warmed.** It scores high on the RFQ classifier — it quotes
 *    our own request back — but extracting it produces enquiry lines for a mail that ANSWERS an
 *    enquiry rather than raising one. That is an LLM call spent to produce something misleading.
 *
 * Same `RFQ_THRESHOLD` as the badge, for the same reason as `shouldPrewarm`: what the user sees
 * and what we spend a call on must not drift apart.
 */
export function shouldPrewarmOnSelect(mail: MailMessage): boolean {
  // OUR OWN outgoing mail is never an enquiry to extract. This needs its own check rather than
  // relying on the reply gate below: `isSupplierReply` returns false for a sent mail (it is not a
  // reply), which used to fall straight through to the confidence test — and our own outgoing RFQ
  // scores 0.75 against a 0.6 threshold, because it is full of RFQ language by construction.
  // Clicking through the Sent folder therefore spent one LLM call per message on mail we wrote.
  if (!isInboundFolder(mail.folder)) return false
  // Our own RFQ fetched back through the purchaser's mailbox (2026-09-19) — never an enquiry.
  if (isOwnOutgoingRfq(mail.subject)) return false
  if (isSupplierReply(mail)) return false
  return classifyRfqMail(mail).confidence >= RFQ_THRESHOLD
}
